

import {
    FilterList as Filter,
    AccountTree as GitBranch,
    List,
    Map as MapIcon,
    Add as Plus,
    Close as X
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';



import type { TimelineFilters } from '@/lib/timeline-utils';
import type { StructureSection, StructureTemplate } from '@/lib/types';

// Built-in templates (same as in structure-selection-gate)
const BUILT_IN_TEMPLATES: Pick<StructureTemplate, 'id' | 'name' | 'defaultSections'>[] = [
  {
    id: 'heros-journey',
    name: "Hero's Journey",
    defaultSections: [
      { id: 'ordinary-world', name: 'Ordinary World', order: 0 },
      { id: 'call-to-adventure', name: 'Call to Adventure', order: 1 },
      { id: 'refusal', name: 'Refusal of the Call', order: 2 },
      { id: 'mentor', name: 'Meeting the Mentor', order: 3 },
      { id: 'threshold', name: 'Crossing the Threshold', order: 4 },
      { id: 'tests', name: 'Tests, Allies, Enemies', order: 5 },
      { id: 'innermost-cave', name: 'Approach to Inmost Cave', order: 6 },
      { id: 'ordeal', name: 'Ordeal', order: 7 },
      { id: 'reward', name: 'Reward', order: 8 },
      { id: 'road-back', name: 'The Road Back', order: 9 },
      { id: 'resurrection', name: 'Resurrection', order: 10 },
      { id: 'return-elixir', name: 'Return with the Elixir', order: 11 },
    ],
  },
  {
    id: 'kishotenketsu',
    name: 'Kishōtenketsu',
    defaultSections: [
      { id: 'ki', name: 'Ki (起) - Introduction', order: 0 },
      { id: 'sho', name: 'Shō (承) - Development', order: 1 },
      { id: 'ten', name: 'Ten (転) - Twist', order: 2 },
      { id: 'ketsu', name: 'Ketsu (結) - Conclusion', order: 3 },
    ],
  },
  {
    id: 'fichtean-curve',
    name: 'Fichtean Curve',
    defaultSections: [
      { id: 'inciting-crisis', name: 'Inciting Crisis', order: 0 },
      { id: 'rising-crisis-1', name: 'Rising Crisis 1', order: 1 },
      { id: 'rising-crisis-2', name: 'Rising Crisis 2', order: 2 },
      { id: 'rising-crisis-3', name: 'Rising Crisis 3', order: 3 },
      { id: 'climax', name: 'Climax', order: 4 },
      { id: 'falling-action', name: 'Falling Action', order: 5 },
    ],
  },
  {
    id: 'three-act',
    name: 'Three-Act Structure',
    defaultSections: [
      { id: 'act-1-setup', name: 'Act I - Setup', order: 0 },
      { id: 'act-2-confrontation', name: 'Act II - Confrontation', order: 1 },
      { id: 'act-3-resolution', name: 'Act III - Resolution', order: 2 },
    ],
  },
  {
    id: 'custom',
    name: 'Custom Structure',
    defaultSections: [
      { id: 'section-1', name: 'Section 1', order: 0 },
    ],
  },
];

interface NarrativeHeaderProps {
  filters: TimelineFilters;
  onClearFilters: () => void;
  onQuickCreate: () => void;
  viewMode: 'list' | 'map' | 'graph';
  onViewModeChange: (_: React.MouseEvent<HTMLElement>, mode: 'list' | 'map' | 'graph' | null) => void;
  structureSections?: StructureSection[];
  selectedStructureTemplateId?: string | null;
  selectedSectionId?: string | null;
  onSectionChange?: (sectionId: string | null) => void;
  onTemplateChange?: (templateId: string) => void;
  onToggleFilters: () => void;
  activeFilterCount: number;
}

export function NarrativeHeader({
  filters,
  onClearFilters,
  onQuickCreate,
  viewMode,
  onViewModeChange,
  structureSections = [],
  selectedStructureTemplateId,
  selectedSectionId,
  onSectionChange,
  onTemplateChange,
  onToggleFilters,
  activeFilterCount,
}: NarrativeHeaderProps) {
  const { t } = useTranslation();

  // Helper to get translated template name
  const getTemplateName = (id: string, fallback: string) => {
    const key = `narrative.templates.${id.replace(/-/g, '_')}.name`;
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  // Get template info
  const selectedTemplate = BUILT_IN_TEMPLATES.find(t => t.id === selectedStructureTemplateId);

  // Get sections from the selected template, sorted by order
  const templateSections = selectedTemplate?.defaultSections
    .sort((a, b) => a.order - b.order)
    .map(s => ({ id: s.id, label: `${s.order}. ${s.name === 'Unassigned' ? t('narrative.selection.unassigned_name') : s.name}` })) || [];

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {/* Page Title */}
        <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>
          {t('narrative.header.title')}
        </Typography>

        {/* Graph view: Structure Template Selector */}
        {viewMode === 'graph' && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('narrative.header.structure')}</InputLabel>
            <Select
              value={selectedStructureTemplateId || ''}
              label={t('narrative.header.structure')}
              onChange={(e) => onTemplateChange?.(e.target.value)}
            >
              {BUILT_IN_TEMPLATES.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {getTemplateName(template.id, template.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Graph view: Section Selector */}
        {viewMode === 'graph' && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('narrative.header.section')}</InputLabel>
            <Select
              value={selectedSectionId || ''}
              label={t('narrative.header.section')}
              onChange={(e) => onSectionChange?.(e.target.value || null)}
            >
              <MenuItem value="">
                <em>{t('narrative.header.all_sections')}</em>
              </MenuItem>
              {templateSections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* View Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={onViewModeChange}
          size="small"
          sx={{ ml: 'auto' }}
        >
          <ToggleButton value="list">
            <List sx={{ fontSize: 18, mr: 1 }} />
            {t('narrative.header.list')}
          </ToggleButton>
          <ToggleButton value="map">
            <MapIcon sx={{ fontSize: 18, mr: 1 }} />
            {t('narrative.header.map')}
          </ToggleButton>
          <ToggleButton value="graph">
            <GitBranch sx={{ fontSize: 18, mr: 1 }} />
            {t('narrative.header.graph')}
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Filter Toggle Button */}
        <Button
          variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
          onClick={onToggleFilters}
          size="small"
          sx={{ position: 'relative' }}
        >
          <Filter sx={{ fontSize: 18, mr: 0.5 }} />
          {t('narrative.header.filters')}
          {activeFilterCount > 0 && (
            <Chip
              label={activeFilterCount}
              size="small"
              color="primary"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.7rem',
                bgcolor: activeFilterCount > 0 ? 'primary.main' : 'default',
                color: 'white',
              }}
            />
          )}
        </Button>

        {/* Quick Create Event Button - always visible, disabled when no section selected in graph view */}
        <Button
          variant="contained"
          startIcon={<Plus sx={{ fontSize: 18 }} />}
          onClick={onQuickCreate}
          disabled={viewMode === 'graph' && !selectedSectionId}
        >
          {t('narrative.header.event')}
        </Button>
      </Stack>

      {/* Active Filter Chips */}
      {activeFilterCount > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
          <Chip
            label={t(activeFilterCount > 1 ? 'timeline.header.filter_active_other' : 'timeline.header.filter_active_one', { count: activeFilterCount })}
            size="small"
            onDelete={onClearFilters}
            deleteIcon={<X sx={{ fontSize: 14 }} />}
          />
        </Stack>
      )}

    </Box>
  );
}
